import React, { useState } from 'react';
import { Card, CardContent, Typography, IconButton, Box, Chip, Grid, CircularProgress, Alert } from '@mui/material';
import { Delete, AccountBalance, Smartphone, Language, CreditCard, Business } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { RootState } from '../../redux/root-reducer';
import { deleteExternalAccount } from '../../redux/account/actions';
import { IExternalAccount } from '../../redux/account/types';
import { DialogueSheet } from '../DialogueSheet/DialogueSheet';

interface ExternalAccountListProps {
    accounts: IExternalAccount[];
}

const ExternalAccountList: React.FC<ExternalAccountListProps> = ({ accounts }) => {
    const dispatch = useDispatch();
    const { themeStyle } = useThemeContext();
    const { deletingExternalAccount } = useSelector((state: RootState) => state.account);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [accountToDelete, setAccountToDelete] = useState<string | null>(null);

    const handleDeleteClick = (accountId: string) => {
        setAccountToDelete(accountId);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (accountToDelete) {
            dispatch(deleteExternalAccount(accountToDelete));
        }
        setDeleteDialogOpen(false);
        setAccountToDelete(null);
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setAccountToDelete(null);
    };

    const getAccountIcon = (accountType: string) => {
        switch (accountType) {
            case 'nuban':
                return <CreditCard />;
            case 'mobile_money':
                return <Smartphone />;
            case 'bank_account':
                return <AccountBalance />;
            case 'paypal':
                return <Business />;
            case 'swift':
                return <Language />;
            default:
                return <AccountBalance />;
        }
    };

    const getAccountTypeLabel = (accountType: string) => {
        switch (accountType) {
            case 'nuban':
                return 'NUBAN';
            case 'mobile_money':
                return 'Mobile Money';
            case 'bank_account':
                return 'Bank Account';
            case 'paypal':
                return 'PayPal';
            case 'swift':
                return 'SWIFT';
            default:
                return accountType;
        }
    };

    const getAccountDetails = (account: IExternalAccount) => {
        switch (account.external_account_type) {
            case 'nuban':
                return {
                    primary: account.bank_details?.bank_name || 'NUBAN Account',
                    secondary: `Account: ${account.bank_details?.account_number}`,
                    code: account.bank_details?.bank_code,
                };
            case 'mobile_money':
                return {
                    primary: account.mobile_money_details?.mobile_money_name || 'Mobile Money',
                    secondary: `Phone: ${account.mobile_money_details?.phone_number}`,
                    code: account.mobile_money_details?.mobile_money_code,
                };
            case 'bank_account':
                return {
                    primary: 'Wire Transfer Account',
                    secondary: `Account: ${account.bank_details?.account_number}`,
                    code: account.bank_details?.routing_number?.toString(),
                };
            case 'paypal':
                return {
                    primary: account.paypal_details?.username || 'PayPal Account',
                    secondary: account.paypal_details?.email,
                    code: 'PayPal',
                };
            default:
                return {
                    primary: account.name,
                    secondary: 'External Account',
                    code: account.external_account_type,
                };
        }
    };

    if (accounts.length === 0) {
        return (
            <Alert severity="info" sx={{ my: 2 }}>
                No external accounts found. Add your first account to get started.
            </Alert>
        );
    }

    return (
        <Box sx={{ mt: 3 }}>
            <Typography
                variant="h6"
                sx={{
                    mb: 2,
                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                    color: theme[themeStyle].tag_white,
                }}>
                Your External Accounts ({accounts.length}/3)
            </Typography>
            <Grid container spacing={2}>
                {accounts.map(account => {
                    const details = getAccountDetails(account);
                    return (
                        <Grid item xs={12} key={account._id}>
                            <Card
                                sx={{
                                    backgroundColor: theme[themeStyle].form_background,
                                    border: `1px solid ${theme[themeStyle].form_divider}`,
                                    '&:hover': {
                                        borderColor: theme[themeStyle].highlight,
                                    },
                                }}>
                                <CardContent>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start',
                                            mb: 2,
                                        }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box
                                                sx={{
                                                    color: theme[themeStyle].primary_orange,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}>
                                                {getAccountIcon(account.external_account_type)}
                                            </Box>
                                            <Chip
                                                label={getAccountTypeLabel(account.external_account_type)}
                                                size="small"
                                                sx={{
                                                    backgroundColor: theme[themeStyle].button_background,
                                                    color: theme[themeStyle].tag_white,
                                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                    fontSize: '0.75rem',
                                                }}
                                            />
                                        </Box>
                                        <IconButton
                                            onClick={() => handleDeleteClick(account._id)}
                                            disabled={deletingExternalAccount}
                                            sx={{
                                                color: '#f44336',
                                                '&:hover': {
                                                    backgroundColor: '#f44336', // + 20
                                                },
                                            }}>
                                            {deletingExternalAccount ? (
                                                <CircularProgress size={20} />
                                            ) : (
                                                <Delete fontSize="small" />
                                            )}
                                        </IconButton>
                                    </Box>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            color: theme[themeStyle].tag_white,
                                            mb: 1,
                                        }}>
                                        {details.primary}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            color: theme[themeStyle].light_gray_text,
                                            mb: 1,
                                        }}>
                                        {details.secondary}
                                    </Typography>
                                    {details.code && (
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                color: theme[themeStyle].placeholderGray,
                                            }}>
                                            Code: {details.code}
                                        </Typography>
                                    )}
                                    <Box
                                        sx={{
                                            mt: 2,
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                        }}>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                color: theme[themeStyle].placeholderGray,
                                            }}>
                                            {account.country}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                color: theme[themeStyle].placeholderGray,
                                            }}>
                                            {account.account_currency}
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>
            <DialogueSheet
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                onAgreeClicked={handleDeleteConfirm}
                onDisagreeClick={handleDeleteCancel}
                header="Delete Account"
                body="Are you sure you want to delete this account? This action cannot be undone."
                positiveText="Delete"
                negativeText="Cancel"
                positiveTextStyle={{ color: '#f44336' }}
            />
        </Box>
    );
};

export default ExternalAccountList;
